import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, createHashRouter, Navigate, RouterProvider, useLocation } from 'react-router';
import { Layout } from './components/Layout';
import { isStaleVersionError, reloadForNewVersion, RouteError } from './components/RouteError';
import { Loading } from './components/ui';
import { isDesktop } from './platform';
import { useAuth } from './state/auth';
import { StorePage } from './pages/StorePage';
import { ProductPage } from './pages/ProductPage';
import { CartPage } from './pages/CartPage';
import { CheckoutCancelPage, CheckoutCompletePage } from './pages/CheckoutPages';
import { ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage, VerifyEmailPage } from './pages/AuthPages';
import { LibraryPage } from './pages/LibraryPage';
import { AccountLayout, DevicesPage, OrdersPage, ProfilePage, RedeemPage, SecurityPage } from './pages/AccountPages';
import { ArticlePage, NewTicketPage, SupportPage, TicketPage } from './pages/SupportPages';
import { DownloadAppPage } from './pages/DownloadAppPage';
import { SettingsPage } from './pages/SettingsPage';
import { UnsubscribePage } from './pages/EmailPages';

// The admin dashboard is its own chunk: customers never download it. A page opened before an
// update can't find the old chunk anymore: it reloads once to get the new version.
const AdminApp = lazy(() =>
  import('./admin/AdminApp').catch((err: unknown) => {
    if (isStaleVersionError(err) && reloadForNewVersion()) return new Promise<never>(() => {});
    throw err;
  }),
);

function RequireAuth({ children, admin }: { children: ReactNode; admin?: boolean }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (admin && user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function NotFound() {
  return (
    <div className="container">
      <div className="empty">
        <h3>Page not found</h3>
        <p>That page doesn't exist (anymore).</p>
        <a className="btn btn-primary" href={isDesktop ? '#/' : '/'}>Back to the store</a>
      </div>
    </div>
  );
}

const routes = [
  {
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <StorePage /> },
      { path: 'products/:slug', element: <ProductPage /> },
      { path: 'cart', element: <CartPage /> },
      { path: 'checkout/complete', element: <CheckoutCompletePage /> },
      { path: 'checkout/cancel', element: <CheckoutCancelPage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'verify-email', element: <VerifyEmailPage /> },
      { path: 'library', element: <RequireAuth><LibraryPage /></RequireAuth> },
      { path: 'redeem', element: <RequireAuth><RedeemPage /></RequireAuth> },
      {
        path: 'account',
        element: <RequireAuth><AccountLayout /></RequireAuth>,
        children: [
          { index: true, element: <ProfilePage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'orders', element: <OrdersPage /> },
          { path: 'devices', element: <DevicesPage /> },
          { path: 'library', element: <Navigate to="/library" replace /> },
        ],
      },
      { path: 'support', element: <SupportPage /> },
      { path: 'support/new', element: <NewTicketPage /> },
      { path: 'support/articles/:slug', element: <ArticlePage /> },
      { path: 'support/tickets/:id', element: <TicketPage /> },
      { path: 'download', element: <DownloadAppPage /> },
      { path: 'settings', element: <RequireAuth><SettingsPage /></RequireAuth> },
      // The unsubscribe link in product update emails opens the store website, never the app.
      ...(isDesktop ? [] : [{ path: 'email/unsubscribe', element: <UnsubscribePage /> }]),
      {
        path: 'admin/*',
        element: (
          <RequireAuth admin>
            <Suspense fallback={<Loading />}>
              <AdminApp />
            </Suspense>
          </RequireAuth>
        ),
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];

// The desktop app loads from file://, so it routes on the URL hash.
const router = isDesktop ? createHashRouter(routes) : createBrowserRouter(routes);

export function App() {
  return <RouterProvider router={router} />;
}
